import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getMarketCondition, updateMarketCondition } from '../services/api';
import { createLogger } from '../utils/debugLogger';
import { useStableCallback } from '../hooks/useStableCallback';

const marketConditionLogger = createLogger(
  'marketConditionContext',
  'Market condition context load/update lifecycle',
  true,
  'ERROR'
);

const MarketConditionContext = createContext();

export const useMarketCondition = () => {
  const context = useContext(MarketConditionContext);
  if (!context) {
    throw new Error('useMarketCondition must be used within a MarketConditionProvider');
  }
  return context;
};

export const MarketConditionProvider = ({ children }) => {
  const [marketCondition, setMarketCondition] = useState('2'); // Default to "Progressing" (50%)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load market condition from database on mount
  useEffect(() => {
    const loadMarketCondition = async () => {
      try {
        setLoading(true);
        const response = await getMarketCondition();
        setMarketCondition(response.data.market_condition);
        setError(null);
      } catch (err) {
        marketConditionLogger.error('Failed to load market condition', err);
        setError('Failed to load market condition');
        // Keep default value if loading fails
      } finally {
        setLoading(false);
      }
    };

    loadMarketCondition();
  }, []);

  // Update market condition in database
  const updateMarketConditionValue = async (newCondition) => {
    try {
      setLoading(true);
      await updateMarketCondition(newCondition);
      setMarketCondition(newCondition);
      setError(null);
      
      // Also update localStorage for backward compatibility
      localStorage.setItem('marketCondition-allAccounts', newCondition);
      
      return true;
    } catch (err) {
      marketConditionLogger.error('Failed to update market condition', err);
      setError('Failed to update market condition');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const stableUpdateMarketCondition = useStableCallback(updateMarketConditionValue);

  const value = useMemo(() => ({
    marketCondition,
    updateMarketCondition: stableUpdateMarketCondition,
    loading,
    error
  }), [marketCondition, stableUpdateMarketCondition, loading, error]);

  return (
    <MarketConditionContext.Provider value={value}>
      {children}
    </MarketConditionContext.Provider>
  );
};
